import React from "react";
import { Edit, Form, Input, Select, DatePicker, useForm, Upload, Button, TimePicker, Alert, InputNumber, Table, Checkbox, Space, Typography } from "@pankod/refine-antd";
import axios from "@pankod/refine-simple-rest/node_modules/axios";
import { API_URL, TOKEN_KEY } from "../../constants/environment";
import { useNavigation } from "@pankod/refine-core";
import { notification } from "@pankod/refine-antd";
import { IWorkPlan } from "../../interfaces/work-plan.interface";
import { impactAxes } from "../../constants/workPlan.constants";
import dayjs from "dayjs";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

interface BudgetSupply {
    key: string;
    supply_name: string;
    quantity: number;
    unit_cost: number;
    total_cost: number;
    requires_support: boolean;
}

export const WorkPlanEdit: React.FC = () => {
    const { formProps, saveButtonProps, queryResult } = useForm<IWorkPlan>();
    const record = queryResult?.data?.data;
    const [fileList, setFileList] = React.useState<any[]>(record?.budget ? [{uid: '-1', name: record.budget, status: 'done', url: record.budget}] : []);
    const [budgetSupplies, setBudgetSupplies] = React.useState<BudgetSupply[]>([]);

    const { push } = useNavigation();

    // Cargar budget items cuando se carga el registro
    React.useEffect(() => {
        if (record?.budget_items && Array.isArray(record.budget_items)) {
            const loadedSupplies = record.budget_items.map((item: any, index: number) => ({
                key: `${Date.now()}-${index}`,
                supply_name: item.supply_name || "",
                quantity: Number(item.quantity) || 0,
                unit_cost: Number(item.unit_cost) || 0,
                total_cost: Number(item.total_cost) || 0,
                requires_support: Boolean(item.requires_support),
            }));
            setBudgetSupplies(loadedSupplies);
        }
    }, [record]);

    const addSupply = () => {
        const newSupply: BudgetSupply = {
            key: Date.now().toString(),
            supply_name: "",
            quantity: 0,
            unit_cost: 0,
            total_cost: 0,
            requires_support: false,
        };
        setBudgetSupplies([...budgetSupplies, newSupply]);
    };

    const removeSupply = (key: string) => {
        setBudgetSupplies(budgetSupplies.filter(supply => supply.key !== key));
    };

    const updateSupply = (key: string, field: keyof BudgetSupply, value: any) => {
        setBudgetSupplies(budgetSupplies.map(supply => {
            if (supply.key === key) {
                const updatedSupply = { ...supply, [field]: value };
                
                // Calcular total_cost automáticamente cuando cambian quantity o unit_cost
                if (field === 'quantity' || field === 'unit_cost') {
                    updatedSupply.total_cost = (updatedSupply.quantity || 0) * (updatedSupply.unit_cost || 0);
                }
                
                return updatedSupply;
            }
            return supply;
        }));
    };

    const getTotalBudget = () => {
        return budgetSupplies.reduce((sum, supply) => sum + supply.total_cost, 0);
    };

    const getTotalWithoutSupport = () => {
        return budgetSupplies
            .filter(supply => !supply.requires_support)
            .reduce((sum, supply) => sum + supply.total_cost, 0);
    };

    const getTotalWithSupport = () => {
        return budgetSupplies
            .filter(supply => supply.requires_support)
            .reduce((sum, supply) => sum + supply.total_cost, 0);
    };
    const onFinish = async (values: any) => {
        const formData = new FormData();
        if (fileList.length > 0 && fileList[0].originFileObj) {
            formData.append("budget", fileList[0].originFileObj);
        }
        console.log(values.start_date);
        formData.append("start_date", typeof values.start_date === "string" ? values.start_date : values.start_date.format("YYYY-MM-DD"));
        formData.append("end_date", typeof values.end_date === "string" ? values.end_date : values.end_date.format("YYYY-MM-DD"));
        formData.append("start_time", typeof values.start_time === "string" ? values.start_time : dayjs(values.start_time).format("HH:mm"));
        formData.append("end_time", typeof values.end_time === "string" ? values.end_time : dayjs(values.end_time).format("HH:mm"));
        formData.append("impact_axis", values.impact_axis || "");
        formData.append("general_objective", values.general_objective || "");
        formData.append("goal", values.goal || "");
        formData.append("strategy", values.strategy || "");
        formData.append("audience", values.audience || "");
        formData.append("scope", values.scope || "");
        formData.append("total_budget", getTotalBudget().toString());

        // Enviar los insumos como campos individuales en FormData
        budgetSupplies.forEach((supply, index) => {
            formData.append(`budget_items[${index}][supply_name]`, supply.supply_name);
            formData.append(`budget_items[${index}][quantity]`, supply.quantity.toString());
            formData.append(`budget_items[${index}][unit_cost]`, supply.unit_cost.toString());
            formData.append(`budget_items[${index}][total_cost]`, supply.total_cost.toString());
            formData.append(`budget_items[${index}][requires_support]`, supply.requires_support ? '1' : '0');
        });

        const token = localStorage.getItem(TOKEN_KEY);
        try {
            await axios.patch(`${API_URL}/work-plans/${record?.id}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            notification.success({
                message: "Plan de trabajo actualizado correctamente",
                description: "El registro fue editado exitosamente.",
            });
            push("/work-plans");
        } catch (error: any) {
            notification.error({
                message: "Error al actualizar",
                description: error?.response?.data?.message || "Ocurrió un error al actualizar el plan de trabajo.",
            });
        }
    };

    const beforeUpload = () => false;
    return (
        <Edit saveButtonProps={saveButtonProps} title={"Editar Actividad"}>
            <Form {...formProps} layout="vertical" onFinish={onFinish}>
                { record?.observation && (
                    <Alert
                        message="Observación"
                        description={`${record.observation}`}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                )}
                <Form.Item
                    name="start_date"
                    label="Start Date"
                    rules={[{ required: true }]}
                    getValueProps={(value) => ({
                        value: value
                        ?
                            (typeof value === "string"
                            ? require("dayjs")(value, "YYYY-MM-DD", true)
                            : require("dayjs")(value))
                        : undefined,
                    })}
                >
                    <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                    label="Hora de inicio del evento"
                    name="start_time"
                    rules={[{ required: true }]}
                    getValueProps={(value) => {
                        const eventDate = formProps.form?.getFieldValue("event_date");
                        if (value && typeof value === "string") {
                        const combinedDateTime = `${dayjs(eventDate).format("YYYY-MM-DD")}T${value}`;
                        return { value: dayjs(combinedDateTime) };
                        }
                    
                        if (value && dayjs.isDayjs(value)) {
                        return { value };
                        }
                    
                        return { value: "" };
                    }}
                >
                    <TimePicker style={{ width: "100%" }} format="HH:mm" />
                </Form.Item>
                <Form.Item
                    name="end_date"
                    label="End Date"
                    rules={[{ required: true }]}
                    getValueProps={(value) => ({
                        value: value
                        ?
                            (typeof value === "string"
                            ? require("dayjs")(value, "YYYY-MM-DD", true)
                            : require("dayjs")(value))
                        : undefined,
                    })}
                >
                    <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                    label="Hora de fin del evento"
                    name="end_time"
                    getValueProps={(value) => {
                    const eventDate = formProps.form?.getFieldValue("event_date");
                            
                    if (value && typeof value === "string") {
                        const combinedDateTime = `${dayjs(eventDate).format("YYYY-MM-DD")}T${value}`;
                        return { value: dayjs(combinedDateTime) };
                    }
                    
                    if (value && dayjs.isDayjs(value)) {
                        return { value };
                    }
                    
                    return { value: "" };
                    }}
                >
                    <TimePicker style={{ width: "100%" }} format="HH:mm" />
                </Form.Item>
                <Form.Item 
                    name="impact_axis" 
                    label="Eje" 
                    rules={[{ required: true }]}
                    getValueProps={(value) => {
                        if (typeof value === "string" && !isNaN(Number(value))) {
                            return { value: Number(value) };
                        }
                        // Si el valor es un objeto, devolver el id
                        if (typeof value === "object" && value !== null && "id" in value) {
                            return { value: value.id };
                        }
                        return { value };
                    }}
                > 
                    <Select 
                        options={impactAxes.map(axis => ({ label: axis.name, value: axis.id }))} 
                        showSearch
                        optionFilterProp="label"
                    />
                </Form.Item>
                <Form.Item name="general_objective" label="Objetivo general" rules={[{ required: true }]} style={{ gridColumn: 'span 5' }}>
                    <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item name="goal" label="Meta" rules={[{ required: true }]} style={{ gridColumn: 'span 5' }}>
                    <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item name="strategy" label="Estrategia" rules={[{ required: true }]}> 
                    <Input />
                </Form.Item>
                <Form.Item name="audience" label="Público" rules={[{ required: true }]}> 
                    <Input />
                </Form.Item>
                <Form.Item name="scope" label="Alcance" rules={[{ required: true }]} style={{ gridColumn: 'span 5' }}>
                    <Input.TextArea rows={2} />
                </Form.Item>
                
                {/* Sección de Presupuesto */}
                <div style={{ gridColumn: 'span 5', marginTop: '20px' }}>
                    <Title level={4}>Presupuesto - Insumos</Title>
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        <Button 
                            type="default" 
                            onClick={addSupply} 
                            icon={<PlusOutlined />}
                            style={{ width: '100%', backgroundColor: '#2b873a', color: '#ffffff' }}
                        >
                            Agregar Insumo
                        </Button>
                        
                        {budgetSupplies.length > 0 && (
                            <Table
                                dataSource={budgetSupplies}
                                pagination={false}
                                bordered
                                size="middle"
                                scroll={{ x: 800 }}
                                summary={() => (
                                    <Table.Summary fixed>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={4} align="right">
                                                <Text strong>Sin Apoyo Vicerrectoría:</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}>
                                                <Text strong style={{ color: '#52c41a' }}>
                                                    ${getTotalWithoutSupport().toFixed(2)}
                                                </Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} />
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={4} align="right">
                                                <Text strong>Con Apoyo Vicerrectoría:</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}>
                                                <Text strong style={{ color: '#1890ff' }}>
                                                    ${getTotalWithSupport().toFixed(2)}
                                                </Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} />
                                        </Table.Summary.Row>
                                        <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                                            <Table.Summary.Cell index={0} colSpan={4} align="right">
                                                <Text strong style={{ fontSize: '16px' }}>Total Presupuesto:</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}>
                                                <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                                                    ${getTotalBudget().toFixed(2)}
                                                </Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} />
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                )}
                            >
                                <Table.Column
                                    title="Nombre del Insumo"
                                    dataIndex="supply_name"
                                    key="supply_name"
                                    width={250}
                                    render={(text, record: BudgetSupply) => (
                                        <Input
                                            value={text}
                                            onChange={(e) => updateSupply(record.key, 'supply_name', e.target.value)}
                                            placeholder="Ingrese nombre del insumo"
                                        />
                                    )}
                                />
                                <Table.Column
                                    title="Cantidad"
                                    dataIndex="quantity"
                                    key="quantity"
                                    width={120}
                                    render={(text, record: BudgetSupply) => (
                                        <InputNumber
                                            value={text}
                                            onChange={(value) => updateSupply(record.key, 'quantity', value || 0)}
                                            min={0}
                                            step={1}
                                            precision={0}
                                            style={{ width: '100%' }}
                                        />
                                    )}
                                />
                                <Table.Column
                                    title="Costo Unitario"
                                    dataIndex="unit_cost"
                                    key="unit_cost"
                                    width={150}
                                    render={(text, record: BudgetSupply) => (
                                        <InputNumber
                                            value={text}
                                            onChange={(value) => updateSupply(record.key, 'unit_cost', value || 0)}
                                            prefix="$"
                                            min={0}
                                            step={0.01}
                                            style={{ width: '100%' }}
                                        />
                                    )}
                                />
                                <Table.Column
                                    title="Costo Total"
                                    dataIndex="total_cost"
                                    key="total_cost"
                                    width={150}
                                    render={(text) => (
                                        <Text strong style={{ textAlign: 'center', display: 'block' }}>${text.toFixed(2)}</Text>
                                    )}
                                />
                                <Table.Column
                                    title="Apoyo Vicerrectoría"
                                    dataIndex="requires_support"
                                    key="requires_support"
                                    width={150}
                                    align="center"
                                    render={(checked, record: BudgetSupply) => (
                                        <Checkbox
                                            checked={checked}
                                            onChange={(e) => updateSupply(record.key, 'requires_support', e.target.checked)}
                                        />
                                    )}
                                />
                                <Table.Column
                                    title="Acciones"
                                    key="actions"
                                    width={100}
                                    align="center"
                                    render={(_, record: BudgetSupply) => (
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => removeSupply(record.key)}
                                        />
                                    )}
                                />
                            </Table>
                        )}
                    </Space>
                </div>
            </Form>
        </Edit>
    );
};

export default WorkPlanEdit;
